import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaSpinner, FaLink, FaUnlink } from 'react-icons/fa';
import { SiStreamlabs } from 'react-icons/si';
import config from '../../config.js';
import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import { useT } from '../../contexts/LocaleContext.jsx';
import '../../styles/BandSettings.css';
import '../../styles/partial/StreamlabsConnect.css';

const STREAMLABS_OAUTH_BASE = 'https://streamlabs.com/';

/**
 * Streamlabs provider card for the Overlay Integration page. Renders as a
 * self-contained card (no page header) so it can sit alongside other
 * providers; the surrounding page owns the title and back navigation.
 */
const StreamlabsConnect = ({ band, connectMessage }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const fetchWrapperRef = useRef(useFetchWrapper());
  const t = useT();

  // Resolves to whether the band is connected, or null when the check failed.
  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetchWrapperRef.current.get(
        `${config.apiBaseUrl}/bands/${band.ID}/streamlabs/status`
      );
      const connected = Boolean(response?.connected);
      setIsConnected(connected);
      return connected;
    } catch (err) {
      console.error('Error fetching Streamlabs status:', err);
      setError(t('overlay.streamlabs.errors.statusFailed'));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [band.ID]);

  // The status is always asked (ENG-516). `?streamlabs_connect=success` is
  // only a hint, and anyone can type it: it used to skip the check and show
  // "Connected" outright, so its banner now waits for the status to agree.
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    fetchStatus().then((connected) => {
      if (cancelled || connectMessage?.type !== 'success' || !connected) return;
      setSuccessMessage(connectMessage.text);
      timer = setTimeout(() => setSuccessMessage(null), 5000);
    });
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [fetchStatus, connectMessage]);

  useEffect(() => {
    if (connectMessage?.type === 'error') setError(connectMessage.text);
  }, [connectMessage]);

  const handleConnect = async () => {
    setIsConnecting(true);
    setError(null);
    try {
      // The backend answers the Streamlabs authorize URL (ENG-471) and builds
      // where Streamlabs sends the browser back itself — this band's page with
      // ?streamlabs_connect=success|error — so nothing is sent but the call.
      const response = await fetchWrapperRef.current.post(
        `${config.apiBaseUrl}/bands/${band.ID}/streamlabs/connect`
      );
      if (response.url && response.url.startsWith(STREAMLABS_OAUTH_BASE)) {
        window.location.href = response.url;
      } else {
        throw new Error('No valid authorization URL received from server');
      }
    } catch (err) {
      console.error('Error connecting Streamlabs:', err);
      setError(t('overlay.streamlabs.errors.connectFailed'));
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    setError(null);
    try {
      await fetchWrapperRef.current.delete(
        `${config.apiBaseUrl}/bands/${band.ID}/streamlabs/disconnect`
      );
      setIsConnected(false);
      setConfirmDisconnect(false);
      setSuccessMessage(t('overlay.streamlabs.success.disconnected'));
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error disconnecting Streamlabs:', err);
      setError(t('overlay.streamlabs.errors.disconnectFailed'));
      setConfirmDisconnect(false);
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <section className="streamlabs-card" aria-labelledby="streamlabs-card-title">
      <div className="streamlabs-card-header">
        <SiStreamlabs className="streamlabs-logo-icon" aria-hidden="true" />
        <div className="streamlabs-card-info">
          <h3 id="streamlabs-card-title" className="streamlabs-card-title">{t('overlay.streamlabs.name')}</h3>
          <p className="streamlabs-card-description">
            {t('overlay.streamlabs.description')}
          </p>
        </div>
        {!isLoading && (
          <div className="streamlabs-status-badge" aria-live="polite">
            {isConnected ? (
              <span className="streamlabs-badge streamlabs-badge--connected">
                <FaCheckCircle aria-hidden="true" /> {t('common.status.connected')}
              </span>
            ) : (
              <span className="streamlabs-badge streamlabs-badge--disconnected">
                {t('common.status.notConnected')}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="streamlabs-card-body">
        {successMessage && (
          <div className="message success-message" role="status">
            <FaCheckCircle className="message-icon" aria-hidden="true" />
            {successMessage}
          </div>
        )}

        {error && (
          <div className="message error-message" role="alert">
            <FaExclamationTriangle className="message-icon" aria-hidden="true" />
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="streamlabs-loading" aria-live="polite" aria-label={t('overlay.streamlabs.checkingLabel')}>
            <FaSpinner className="spinning" aria-hidden="true" />
            <span>{t('overlay.status.checking')}</span>
          </div>
        ) : isConnected ? (
          <div className="streamlabs-actions">
            <p className="streamlabs-connected-message">
              {t('overlay.streamlabs.connectedMessage')}
            </p>
            {confirmDisconnect ? (
              <div className="streamlabs-confirm-disconnect" role="group" aria-label={t('overlay.streamlabs.confirmGroupLabel')}>
                <p className="streamlabs-confirm-text">
                  {t('overlay.streamlabs.confirmText')}
                </p>
                <div className="streamlabs-confirm-buttons">
                  <button
                    className="form-button cancel-button"
                    onClick={() => setConfirmDisconnect(false)}
                    disabled={isDisconnecting}
                  >
                    {t('common.actions.cancel')}
                  </button>
                  <button
                    className="form-button streamlabs-disconnect-btn"
                    onClick={handleDisconnect}
                    disabled={isDisconnecting}
                  >
                    {isDisconnecting ? (
                      <>
                        <FaSpinner className="spinning" aria-hidden="true" />
                        {t('overlay.status.disconnecting')}
                      </>
                    ) : (
                      <>
                        <FaUnlink aria-hidden="true" />
                        {t('overlay.streamlabs.confirmDisconnect')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="form-button streamlabs-disconnect-btn"
                onClick={() => setConfirmDisconnect(true)}
                aria-label={t('overlay.streamlabs.disconnect')}
              >
                <FaUnlink aria-hidden="true" />
                {t('overlay.streamlabs.disconnect')}
              </button>
            )}
          </div>
        ) : (
          <div className="streamlabs-actions">
            <button
              className="form-button streamlabs-connect-btn"
              onClick={handleConnect}
              disabled={isConnecting}
              aria-label={t('overlay.streamlabs.connect')}
            >
              {isConnecting ? (
                <>
                  <FaSpinner className="spinning" aria-hidden="true" />
                  {t('overlay.status.connecting')}
                </>
              ) : (
                <>
                  <FaLink aria-hidden="true" />
                  {t('overlay.streamlabs.connect')}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default StreamlabsConnect;
